import { useMemo } from 'react';

import { useBetSlice } from '@pp/core/src/store/slices/bets/selectors';
import { useGameStateSlice } from '@pp/core/src/store/slices/game/selectors';
import { GameStatus } from '@pp/core/src/store/slices/game/types';

import { Bets } from '../../../config/bets';
import { useHighflyerStateSlice } from '../../../store/slices/highflyerState/selectors';

import { useIsBetConfirmedConnected } from './useIsBetConfirmedConnected';
import { useValidBetInputValue } from './useValidBetInputValue';

export const useQuickBetDisabled = (betId: Bets, chipAmount: number, isNewQuickBetBehaviour: boolean): boolean => {
  const currentStatus = useGameStateSlice.getCurrentStatus();
  const betLimits = useBetSlice.betLimits() ?? {};
  const { betInputAmount } = useValidBetInputValue(betId);
  const remainingBalance = useHighflyerStateSlice.balance();
  const isBetConfirmed = useIsBetConfirmedConnected(betId);
  const hasEarlyBets = useHighflyerStateSlice.hasEarlyBets(betId);
  const isDealingIngProgress = useHighflyerStateSlice.isDealingInProgress(betId);

  return useMemo(() => {
    let isDisabled = false;
    const dynamicRemainingBalance = remainingBalance - betInputAmount;
    const isLowBalance = chipAmount > dynamicRemainingBalance;
    if (isBetConfirmed) {
      isDisabled = true;
    }
    if (
      [GameStatus.Dealing, GameStatus.WaitForNextGame, GameStatus.Result].includes(currentStatus as GameStatus) &&
      !isDealingIngProgress
    ) {
      isDisabled = false;
    }
    if (hasEarlyBets) {
      isDisabled = true;
    }
    // as per new CR LCE-111938 no need to disable this any more.
    if (!isNewQuickBetBehaviour && isLowBalance) {
      isDisabled = true;
    }
    let isBetsMoreThenQuickBets = false;
    if (!isNewQuickBetBehaviour) {
      isBetsMoreThenQuickBets = betInputAmount + chipAmount > betLimits[betId].max;
    }
    return isDisabled || isBetsMoreThenQuickBets;
  }, [currentStatus, remainingBalance, betInputAmount, isBetConfirmed, isDealingIngProgress, hasEarlyBets]);
};
